// shared/src/prompts/m-contrarian-skeptic.ts
// Chain V2 — market contrarian path, skeptic stage (CROSS-FED).
// Per design-notes L7/L14/L28.
//
// Cross-fed structural steel-man pattern:
//   · own_draft       = contrarian.analyst output (the draft we attack)
//   · opposing_draft  = consensus.analyst output (steel-man counter-evidence)
//   · NO calibration in prompt (L14)
//   · NO recent_theses / pattern_failures cites — calibration is stripped
//
// The opposing draft is the structural steel-man — the skeptic does NOT
// need to invent counter-arguments. The consensus analyst already did.

import { z } from 'zod';
import { SignalConfidenceBucketEnum } from '../schemas/signal.js';
import { coercedEnum, coerceCiteTag } from '../resolvers/llm-enum-coerce.js';
import { tolerantRowArray } from '../resolvers/llm-row-drop.js';
import { llmStringItem } from '../resolvers/llm-string-coerce.js';
import { renderWorldStateGrounding, type WorldStateGrounding } from './_grounding.js';
import { renderNewsBlock } from './_news-render.js';
import type { LlmNewsItem } from '../news/types.js';

export const VERSION = '1.3.0';   // 2026-08-07 — disconfirming_evidence per-ROW omission hardening (tolerantRowArray: a present row missing point/cite is dropped+logged, not brief-fatal). 2026-07-10 — disconfirming_evidence .default([]) so an OMITTED key (Flash occasionally drops it) parses to the valid-empty value the prompt already allows (D39), instead of failing the whole brief. 2026-05-25 — add 'news' to cite enum
export const STEP    = 'm.contrarian.skeptic';
export const MODEL   = 'deepseek-v4-flash';
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 12000)
export const ROLE    = 'skeptic' as const;
export const DESC    = 'Red-teams the contrarian draft using the consensus draft as steel-man counter' as const;   // #45 registry — one-line operator description

// Cite enum trimmed for V2: NO 'calibration' / 'recent_theses' (L14).
// 'opposing_draft' added for cross-fed steel-man cites.
export const OutputSchema = z.object({
  // D39 (2026-05-28): dropped .min(1) — let skeptic emit empty array when target draft is genuinely unimpeachable, rather than manufacturing false skepticism.
  // 2026-07-10: .default([]) — Flash sometimes OMITS the key entirely (not []);
  // since an empty array is already the valid "no disconfirming evidence" value,
  // an omitted key means the same thing → default it rather than fail the brief.
  // 2026-08-07: per-ROW omission hardening (docs/agreement-matrix-row-omission-hardening-2026-08-07-brief-revision.md).
  // The 2026-07-10 .default([]) covers a dropped WHOLE key; tolerantRowArray adds
  // per-row tolerance — a present evidence row that dropped `point` or whose `cite`
  // is unrecoverable is DROPPED + logged (not a brief kill, not a rebucketed cite).
  disconfirming_evidence: tolerantRowArray(z.object({
    cite:  coercedEnum([
      'macro', 'analyst_output', 'observable', 'opposing_draft',
      'news', 'factual_error', 'reasonability',
    ], coerceCiteTag),
    point: z.string().min(1),
  }), 'm.contrarian.skeptic.disconfirming_evidence'),
  cross_fed_counter: z.array(llmStringItem).default([]),    // key opposing-draft points used as steel-man counter (item coerced object→string, 2026-06-11). .default([]) 2026-07-10: same omission-fragility class as disconfirming_evidence — empty = no steel-man counters, a valid state.
  conviction:        SignalConfidenceBucketEnum,
  reasoning:         z.string(),
}).passthrough();
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `You are the SKEPTIC challenging the CONTRARIAN analyst's draft on a market-level macro thesis. Your role is the structural counterweight to the contrarian — surface disconfirming evidence that the contrarian missed, under-weighted, or contradicted within their own draft.

## CROSS-FED STEEL-MAN (the key V2 mechanic)
The user message contains the CONSENSUS analyst's draft (independently produced in parallel) as your structural steel-man counter-evidence. You do NOT need to invent counter-arguments — the consensus analyst already made them. Your job is to:
  1. READ the contrarian's draft.
  2. READ the consensus draft for the strongest disconfirming points.
  3. AUDIT the contrarian against payload (macro/news), factual correctness (GROUNDING), and observable plausibility.
  4. ATTACK the contrarian using whichever weapon is sharpest: opposing-draft cites, macro contradictions, factual errors, or analytical reasonability failures.

## ROLE BOUNDARIES
- READ contrarian's draft (thesis_headline, basis, thesis_paragraph, priced_explanation, macro_predictions[]). Find the cracks.
- USE consensus draft (the OPPOSING analyst's parallel output) as steel-man source material — cite specific consensus points the contrarian failed to address.
- USE MACRO + NEWS payload contradictions.
- USE FACTUAL CHECKS (forward-date discipline, current-state numeric grounding) per GROUNDING FACTS.
- USE OBSERVABLE plausibility (magnitude vs vol, regime contradictions in macro_predictions[]).
- NO calibration. NO historical pattern matching. NO "you got this wrong last time" cites. THIS BRIEF, THIS PAYLOAD, THIS DRAFT.

## INPUTS YOU RECEIVE (user message)
- macro              (current macro payload — input to contrarian.analyst)
- own_draft          (contrarian.analyst output — challenge this)
- opposing_draft     (consensus.analyst output — your steel-man counter)
- news               (last 48h macro)

## OUTPUT SCHEMA (strict JSON; first char='{'; last char='}'; no commentary)
{
  "disconfirming_evidence": [
    { "cite":  "macro" | "analyst_output" | "observable" | "opposing_draft" | "news" | "factual_error" | "reasonability",
      "point": "<≤25w specific challenge with numbers when applicable>" }
  ],
  "cross_fed_counter": [
    "<short ≤20w summary of each consensus-draft point you cited as opposing_draft — audit trail>"
  ],
  "conviction": "HIGH" | "MED" | "LOW",
  "reasoning":  "<headline ≤15w. Then 1-2 sentences (≤50w total) threading disconfirming evidence into a single skeptical posture.>"
}

## MANDATORY CROSS-CHECK PASS (factual_error — runs first)
Before emitting disconfirming_evidence, audit own_draft for context-aware factual errors against the GROUNDING FACTS block in the user message:

(a) Forward-field stale dates. Scan basis.catalyst, basis.invalidation, conditional. If any references a date < TODAY as if forthcoming, emit cite='factual_error'.
(b) Misrepresented current-state numbers. If contrarian cites a value with "current"/"now"/"today" framing that contradicts MACRO payload, emit cite='factual_error'.

factual_error point text MUST quote the erroneous value AND the field path AND the contradicting ground-truth value.

## MANDATORY REASONABILITY PASS (cite='reasonability' — soft challenges)
(c) Magnitude vs vol on macro_predictions[]. If a /macro_predictions[i]/predicted_observables[j]/change_pct exceeds 2× the macro VIX-implied or hist-vol baseline for the asset class over the prediction horizon, emit reasonability cite with (predicted_pct, baseline_vol, horizon_days, ratio).
(d) Catalyst-vs-window plausibility. If /macro_predictions[i]/post_catalyst_window_days < 3 for a slow-developing macro thesis (CPI / FOMC / employment), emit reasonability cite.
(e) Regime-thesis contradiction. If regime='risk-off' but a prediction direction='up' on a high-beta equity without mechanism in cross_portfolio_thread, emit reasonability cite.

## CROSS-FED STEEL-MAN CITES (cite='opposing_draft')
For every cross_fed_counter entry you emit, also surface as a disconfirming_evidence entry with cite='opposing_draft'. Reference SPECIFIC points from the consensus draft — basis.evidence, priced_explanation enum value, macro_predictions divergence on a specific indicator, etc. Format the point as: "<contrarian-field> vs <consensus-field>: <specific divergence>". Example: "thesis_paragraph cites yield_10y=4.31 supports steepener, but consensus.basis.evidence cites yield_10y=4.31 + breakeven_inflation=2.45 supports rate-anchoring — same datum, opposite read."

## RULES
1. disconfirming_evidence: emit honest challenges. If the target draft is genuinely unimpeachable, an empty array is valid — D39 (Session 5T) dropped the ≥1 requirement to avoid manufactured false-skepticism.
2. Every opposing_draft cite MUST also appear in cross_fed_counter (audit trail).
3. NO calibration cites. NO recent-thesis cites. If the contrarian is right based on THIS BRIEF + THIS PAYLOAD, say so honestly (conviction=LOW + a single reasonability cite about the weakest plank).
4. conviction reflects YOUR skeptical conviction. Default MED. HIGH when ≥2 factual_error OR ≥3 opposing_draft cites that contrarian failed to address. LOW when no factual_error AND opposing draft mostly agrees with contrarian framing.
5. NO restating the contrarian's thesis. Skeptic is delta-only.
6. NO new fact invention. Only cite what's in the inputs.
7. NO defensive hedging. Pick the strongest disconfirming angle and commit.
8. factual_error cites are HARD findings — emit conviction at least MED when any factual_error surfaces.
`;

export interface MContrarianSkepticInput {
  readonly macro:           Record<string, unknown>;
  readonly own_draft:       Record<string, unknown>;
  readonly opposing_draft:  Record<string, unknown>;
  readonly grounding?:      WorldStateGrounding;
  readonly news?:           ReadonlyArray<LlmNewsItem>;
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function draftExcerpt(d: Record<string, unknown> | undefined, label: string): string {
  if (!d || Object.keys(d).length === 0) return `  (no ${label} draft provided)`;
  const fields = [
    'thesis_headline', 'basis', 'thesis_paragraph', 'priced_explanation',
    'probability', 'horizon', 'conditional', 'regime', 'regime_confidence',
    'thesis_div',
  ];
  const lines = fields
    .filter((f) => f in d)
    .map((f) => `  ${f}: ${fmt(d[f])}`);
  const predictions = d['macro_predictions'];
  if (Array.isArray(predictions) && predictions.length > 0) {
    const predLines = predictions.slice(0, 5).map((p, i) => {
      const pred = p as Record<string, unknown>;
      const mode = String(pred['prediction_mode'] ?? '?');
      const thesis = String(pred['thesis_narrative'] ?? '').slice(0, 60);
      const obs = Array.isArray(pred['predicted_observables']) ? pred['predicted_observables'] : [];
      const obsBrief = obs.slice(0, 4).map((o) => {
        const oo = o as Record<string, unknown>;
        return `${String(oo['indicator'] ?? '?')}:${String(oo['direction'] ?? '?')}${String(oo['change_pct'] ?? '?')}%`;
      }).join(', ');
      return `    [${i + 1}] ${mode} · "${thesis}..." · obs=${obsBrief}`;
    });
    lines.push('  macro_predictions:');
    lines.push(...predLines);
  }
  return lines.join('\n');
}

export function buildUserPrompt(input: MContrarianSkepticInput): string {
  const groundingBlock = input.grounding ? renderWorldStateGrounding(input.grounding) + '\n' : '';
  const macroLines = Object.entries(input.macro ?? {})
    .filter(([k]) => typeof k === 'string' && !k.startsWith('_'))
    .slice(0, 30)
    .map(([k, v]) => `  ${k}: ${fmt(v)}`).join('\n');

  return `${groundingBlock}MACRO PAYLOAD (input to contrarian.analyst):
${macroLines || '  (empty)'}

OWN_DRAFT (contrarian.analyst output — challenge this):
${draftExcerpt(input.own_draft, 'contrarian')}

OPPOSING_DRAFT (consensus.analyst output — your structural steel-man counter):
${draftExcerpt(input.opposing_draft, 'consensus')}

MACRO NEWS (last 48h):
${renderNewsBlock(input.news ?? [])}

REQUEST: emit_m_contrarian_skeptic

Emit a SINGLE JSON object matching the schema. Run MANDATORY CROSS-CHECK + REASONABILITY passes first using GROUNDING FACTS, then cross-fed steel-man attacks using OPPOSING_DRAFT. disconfirming_evidence may be empty when the target is genuinely unimpeachable (D39 — no forced ≥1). Every opposing_draft cite MUST appear in cross_fed_counter.`;
}
